import axios from 'axios';

const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '' || window.location.port === '3000';
const MANGA_API_DOMAIN = 'https://manga.whitewhale.help';
const MANGA_STORAGE_FALLBACK = isLocal
    ? 'https://manga-storage.whitewhale.help'
    : null;
const AUTH_DOMAIN = 'https://api.whitewhale.help';

const RATE_LIMIT_EVENT = 'manga-rate-limit';
const MIN_RATE_LIMIT_NOTICE_MS = 5000;
let lastRateLimitNoticeAt = 0;

const parseRetryAfterSeconds = (value) => {
    if (!value) {
        return null;
    }
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
        return numeric;
    }
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) {
        const deltaSeconds = Math.ceil((parsed - Date.now()) / 1000);
        return deltaSeconds > 0 ? deltaSeconds : null;
    }
    return null;
};

const buildRateLimitMessage = (retryAfterSeconds) => {
    if (retryAfterSeconds && retryAfterSeconds > 0) {
        return `Слишком много запросов. Попробуйте через ${retryAfterSeconds} с.`;
    }
    return 'Слишком много запросов. Попробуйте чуть позже.';
};

const notifyRateLimit = (retryAfterSeconds) => {
    if (typeof window === 'undefined' || !window.dispatchEvent) {
        return;
    }
    const now = Date.now();
    const cooldown = Math.max(MIN_RATE_LIMIT_NOTICE_MS, retryAfterSeconds ? retryAfterSeconds * 1000 : 0);
    if (now - lastRateLimitNoticeAt < cooldown) {
        return;
    }
    lastRateLimitNoticeAt = now;
    const message = buildRateLimitMessage(retryAfterSeconds);
    window.dispatchEvent(new CustomEvent(RATE_LIMIT_EVENT, {
        detail: { message, retryAfter: retryAfterSeconds },
    }));
};

const mangaClient = axios.create();
mangaClient.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = error?.response?.status;
        if (status === 429) {
            const retryHeader = error?.response?.headers?.['retry-after'] || error?.response?.headers?.['Retry-After'];
            const retryAfterSeconds = parseRetryAfterSeconds(retryHeader);
            error.message = buildRateLimitMessage(retryAfterSeconds);
            error.rateLimit = true;
            error.retryAfter = retryAfterSeconds;
            // The review form explains its own limit; the generic toast would only repeat it.
            const isReviewSave = error?.config?.method === 'put' && /\/reviews$/.test(error?.config?.url || '');
            if (!isReviewSave) {
                notifyRateLimit(retryAfterSeconds);
            }
        }
        return Promise.reject(error);
    }
);

let jwtInFlight = null;
let storageTargetInFlight = null;
let storageResolveInFlight = new Map();

let defaultStorageBaseUrl = null;
let storageBaseByBucket = {};
let resolvedFileUrlCache = new Map();

const rememberStorageBase = (bucket, baseUrl) => {
    if (!baseUrl) {
        return;
    }
    defaultStorageBaseUrl = baseUrl;
    if (bucket) {
        storageBaseByBucket[bucket] = baseUrl;
    }
};

const JWT_STORAGE_KEY = 'mangaJwt';
const JWT_EXP_STORAGE_KEY = 'mangaJwtExpiresAt';
const JWT_SESSION_STORAGE_KEY = 'mangaJwtSessionKey';

const clearStoredJwt = () => {
    localStorage.removeItem(JWT_STORAGE_KEY);
    localStorage.removeItem(JWT_EXP_STORAGE_KEY);
    localStorage.removeItem(JWT_SESSION_STORAGE_KEY);
};

const decodeJwtPayload = (token) => {
    if (!token || typeof token !== 'string') {
        return null;
    }
    const parts = token.split('.');
    if (parts.length < 2) {
        return null;
    }
    let payload = parts[1];
    if (!payload) {
        return null;
    }
    payload = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padding = payload.length % 4;
    if (padding) {
        payload += '='.repeat(4 - padding);
    }
    try {
        return JSON.parse(atob(payload));
    } catch (error) {
        return null;
    }
};

const getJwtExpiry = (token) => {
    const payload = decodeJwtPayload(token);
    const exp = Number(payload?.exp);
    if (!Number.isFinite(exp) || exp <= 0) {
        return null;
    }
    return exp * 1000;
};

const resolveJwtExpiry = (token, storedExp) => {
    const tokenExp = getJwtExpiry(token);
    if (!tokenExp) {
        return null;
    }
    const stored = Number.isFinite(storedExp) && storedExp > 0 ? storedExp : null;
    if (tokenExp && stored) {
        return Math.min(tokenExp, stored);
    }
    return tokenExp;
};

const isJwtAlive = (token, storedExp) => {
    const exp = resolveJwtExpiry(token, storedExp);
    if (!exp) {
        return false;
    }
    return exp - 60000 > Date.now();
};

const readStoredJwt = () => {
    const token = localStorage.getItem(JWT_STORAGE_KEY);
    const exp = Number(localStorage.getItem(JWT_EXP_STORAGE_KEY) || 0);
    const sessionKey = localStorage.getItem('sessionKey');
    const storedSession = localStorage.getItem(JWT_SESSION_STORAGE_KEY);
    if (!sessionKey || (storedSession && storedSession !== sessionKey)) {
        clearStoredJwt();
        return null;
    }
    if (!token) {
        return null;
    }
    if (!isJwtAlive(token, exp)) {
        clearStoredJwt();
        return null;
    }
    return { token, exp: resolveJwtExpiry(token, exp) };
};

const storeJwt = (token, expiresIn, sessionKey) => {
    const ttlSeconds = Number(expiresIn || 0);
    const expFromTtl = ttlSeconds > 0 ? Date.now() + ttlSeconds * 1000 : null;
    const exp = resolveJwtExpiry(token, expFromTtl);
    if (!exp || exp <= Date.now()) {
        clearStoredJwt();
        return null;
    }
    localStorage.setItem(JWT_STORAGE_KEY, token);
    localStorage.setItem(JWT_EXP_STORAGE_KEY, exp.toString());
    if (sessionKey) {
        localStorage.setItem(JWT_SESSION_STORAGE_KEY, sessionKey);
    }
    return exp;
};

const getJwt = async ({ force = false } = {}) => {
    const sessionKey = localStorage.getItem('sessionKey');
    if (!sessionKey) {
        return null;
    }

    const stored = readStoredJwt();
    if (!force && stored && stored.exp > Date.now()) {
        return stored.token;
    }

    if (jwtInFlight) {
        return jwtInFlight;
    }

    jwtInFlight = axios.post(`${AUTH_DOMAIN}/auth/jwt`, null, {
        headers: {
            Authorization: `Bearer ${sessionKey}`,
        },
    }).then((response) => {
        if (response.data?.type === 'success' && response.data?.data?.token) {
            const token = response.data.data.token;
            const exp = storeJwt(token, response.data.data.expiresIn, sessionKey);
            if (!exp || !isJwtAlive(token, exp)) {
                clearStoredJwt();
                throw new Error('JWT expired');
            }
            return token;
        }
        const message = response.data?.message || 'JWT exchange failed';
        throw new Error(message);
    }).finally(() => {
        jwtInFlight = null;
    });

    return jwtInFlight;
};

const getAuthHeaders = async (required = false) => {
    try {
        let token = await getJwt();
        if (token && !isJwtAlive(token, Number(localStorage.getItem(JWT_EXP_STORAGE_KEY) || 0))) {
            clearStoredJwt();
            token = await getJwt({ force: true });
        }
        if (!token) {
            if (required) {
                throw new Error('Session required');
            }
            return {};
        }
        return { Authorization: `Bearer ${token}` };
    } catch (error) {
        if (required) {
            throw error;
        }
        return {};
    }
};

const normalizeStorageTarget = (payload) => {
    if (!payload || typeof payload !== 'object') {
        return {};
    }
    return {
        bucket: payload.bucket || payload.bucketName || payload.bucket_name,
        publicBaseUrl: payload.publicBaseUrl || payload.public_base_url || payload.baseUrl || payload.url,
        fileUrl: payload.fileUrl || payload.file_url,
    };
};

const makeResolveKey = (bucket, fileId) => `${bucket || ''}:${fileId || ''}`;

const resolveStorageLocation = async (bucket, fileId, { required = false } = {}) => {
    if (!bucket) {
        return null;
    }
    const key = makeResolveKey(bucket, fileId);
    if (fileId) {
        const cached = resolvedFileUrlCache.get(key);
        if (cached) {
            return { bucket, fileUrl: cached, publicBaseUrl: storageBaseByBucket[bucket] || null };
        }
    }
    if (!fileId && storageBaseByBucket[bucket]) {
        return { bucket, publicBaseUrl: storageBaseByBucket[bucket] };
    }
    if (storageResolveInFlight.has(key)) {
        return storageResolveInFlight.get(key);
    }

    const promise = (async () => {
        try {
            const headers = await getAuthHeaders(false);
            const params = { bucket };
            if (fileId !== undefined && fileId !== null) {
                params.id = fileId;
            }
            const response = await mangaClient.get(`${MANGA_API_DOMAIN}/storage/resolve`, { params, headers });
            const payload = response.data?.data || response.data;
            const resolved = normalizeStorageTarget(payload);
            if (resolved.publicBaseUrl) {
                rememberStorageBase(bucket, resolved.publicBaseUrl);
            }
            if (resolved.fileUrl) {
                resolvedFileUrlCache.set(key, resolved.fileUrl);
            }
            return resolved;
        } catch (error) {
            if (required) {
                throw error;
            }
            return null;
        } finally {
            storageResolveInFlight.delete(key);
        }
    })();

    storageResolveInFlight.set(key, promise);
    return promise;
};

const getStorageTarget = async ({ required = false } = {}) => {
    if (storageTargetInFlight) {
        return storageTargetInFlight;
    }

    storageTargetInFlight = (async () => {
        const headers = await getAuthHeaders(required);
        try {
            const response = await mangaClient.get(`${MANGA_API_DOMAIN}/storage/target`, { headers });
            const payload = response.data?.data || response.data;
            const target = normalizeStorageTarget(payload);
            if (target.publicBaseUrl) {
                rememberStorageBase(target.bucket, target.publicBaseUrl);
            }
            return target;
        } catch (error) {
            if (required) {
                throw error;
            }
            return {};
        } finally {
            storageTargetInFlight = null;
        }
    })();

    return storageTargetInFlight;
};

const resolveStorageBaseUrl = (item) => {
    if (!item || typeof item !== 'object') {
        return null;
    }
    return item.publicBaseUrl
        || item.public_base_url
        || item.storageBaseUrl
        || item.storageUrl
        || item.storageDomain
        || item.baseUrl
        || item.bucketBaseUrl
        || null;
};

const pickStorageBaseUrl = (bucket, storageBaseUrl) => {
    if (storageBaseUrl) {
        return storageBaseUrl;
    }
    if (bucket && storageBaseByBucket[bucket]) {
        return storageBaseByBucket[bucket];
    }
    if (defaultStorageBaseUrl) {
        return defaultStorageBaseUrl;
    }
    if (MANGA_STORAGE_FALLBACK) {
        return MANGA_STORAGE_FALLBACK;
    }
    return null;
};

const extractFileMeta = (item, fallbackBucket) => {
    if (item === null || item === undefined) {
        return { fileId: null, bucket: fallbackBucket };
    }
    if (typeof item === 'string' || typeof item === 'number') {
        return { fileId: item, bucket: fallbackBucket };
    }
    return {
        fileId: item.fileId || item.id || item.storageFileId || item.file_id,
        bucket: item.bucket || item.storageBucket || item.storage_bucket || item.bucketName || fallbackBucket,
    };
};

const buildStorageUrl = (fileId, bucket, storageBaseUrl) => {
    if (!fileId) {
        return null;
    }
    const base = pickStorageBaseUrl(bucket, storageBaseUrl);
    if (!base) {
        return null;
    }
    if (bucket) {
        return `${base}/buckets/${bucket}/files/${fileId}`;
    }
    return `${base}/files/${fileId}`;
};

const resolveFileUrl = (item, fallbackBucket) => {
    if (!item) {
        return null;
    }
    if (typeof item === 'string' || typeof item === 'number') {
        return buildStorageUrl(item, fallbackBucket);
    }
    const direct = item.url || item.href || item.link;
    if (direct) {
        return direct;
    }
    const fileUrl = item.fileUrl || item.file_url;
    if (fileUrl) {
        return fileUrl;
    }
    const { fileId, bucket } = extractFileMeta(item, fallbackBucket);
    const baseUrl = resolveStorageBaseUrl(item);
    if (baseUrl) {
        rememberStorageBase(bucket, baseUrl);
    }
    if (fileId && bucket) {
        const cached = resolvedFileUrlCache.get(makeResolveKey(bucket, fileId));
        if (cached) {
            return cached;
        }
    }
    return buildStorageUrl(fileId, bucket, baseUrl);
};

const resolveFileUrlAsync = async (item, fallbackBucket) => {
    if (!item) {
        return null;
    }
    const direct = item.url || item.href || item.link || item.fileUrl || item.file_url;
    if (direct) {
        return direct;
    }
    const { fileId, bucket } = extractFileMeta(item, fallbackBucket);
    if (!fileId || !bucket) {
        return null;
    }
    const cached = resolvedFileUrlCache.get(makeResolveKey(bucket, fileId));
    if (cached) {
        return cached;
    }
    const resolved = await resolveStorageLocation(bucket, fileId);
    if (resolved?.fileUrl) {
        return resolved.fileUrl;
    }
    if (resolved?.publicBaseUrl) {
        return buildStorageUrl(fileId, bucket, resolved.publicBaseUrl);
    }
    return null;
};

const resolveCoverUrl = (manga) => {
    if (!manga) {
        return null;
    }
    if (typeof manga.cover === 'string') {
        return manga.cover;
    }
    if (typeof manga.poster === 'string') {
        return manga.poster;
    }
    const direct = manga.coverUrl || manga.cover?.url || manga.cover?.href;
    if (direct) {
        return direct;
    }
    const fileUrl = manga.coverFileUrl || manga.cover_file_url || manga.cover?.fileUrl || manga.cover?.file_url;
    if (fileUrl) {
        return fileUrl;
    }
    const cover = manga.cover || manga.poster || manga.logo || {};
    const fileId = manga.coverFileId || manga.cover_file_id || cover.fileId || cover.id || manga.logoFileId;
    const bucket = manga.coverStorageBucket || manga.cover_storage_bucket || cover.bucket || manga.logoStorageBucket;
    const baseUrl = resolveStorageBaseUrl(cover) || resolveStorageBaseUrl(manga);
    if (baseUrl) {
        rememberStorageBase(bucket, baseUrl);
    }
    if (fileId && bucket) {
        const cached = resolvedFileUrlCache.get(makeResolveKey(bucket, fileId));
        if (cached) {
            return cached;
        }
    }
    return buildStorageUrl(fileId, bucket, baseUrl);
};

const resolveCoverUrlAsync = async (manga) => {
    if (!manga) {
        return null;
    }
    const direct = resolveCoverUrl(manga);
    if (direct) {
        return direct;
    }
    const cover = manga.cover || manga.poster || manga.logo || {};
    const fileId = manga.coverFileId || manga.cover_file_id || cover.fileId || cover.id || manga.logoFileId;
    const bucket = manga.coverStorageBucket || manga.cover_storage_bucket || cover.bucket || manga.logoStorageBucket;
    if (!fileId || !bucket) {
        return null;
    }
    const resolved = await resolveStorageLocation(bucket, fileId);
    if (resolved?.fileUrl) {
        return resolved.fileUrl;
    }
    if (resolved?.publicBaseUrl) {
        return buildStorageUrl(fileId, bucket, resolved.publicBaseUrl);
    }
    return null;
};

const MangaApiService = {
    isAllowedDomain: () => true,
    getJwt,
    getStorageTarget,
    resolveStorageBaseUrl,
    buildStorageUrl,
    resolveFileUrl,
    resolveFileUrlAsync,
    resolveCoverUrl,
    resolveCoverUrlAsync,

    searchMangas: async (query = '', options = {}) => {
        const headers = await getAuthHeaders(false);
        const includeDeleted = options?.includeDeleted;
        const params = {
            ...(query ? { q: query } : {}),
            ...(includeDeleted ? { includeDeleted: true } : {}),
        };
        if (options?.page !== undefined && options?.page !== null) {
            params.page = options.page;
        }
        if (options?.size !== undefined && options?.size !== null) {
            params.size = options.size;
        }
        if (Array.isArray(options?.genres) && options.genres.length > 0) {
            params.genres = options.genres.join(',');
        }
        if (options?.completed === true || options?.completed === false) {
            params.completed = options.completed;
        }
        if (options?.order) {
            params.order = options.order;
        }
        const response = await mangaClient.get(`${MANGA_API_DOMAIN}/mangas/search`, {
            params,
            headers,
        });
        return response.data;
    },

    getUserMangaListsOf: async (userId, status) => {
        const headers = await getAuthHeaders(false);
        const response = await mangaClient.get(`${MANGA_API_DOMAIN}/users/${userId}/manga-lists`, {
            params: status ? { status } : {},
            headers,
        });
        return response.data;
    },

    getUserMangaProfile: async (userId) => {
        const headers = await getAuthHeaders(false);
        const response = await mangaClient.get(`${MANGA_API_DOMAIN}/users/${userId}/manga-profile`, { headers });
        return response.data;
    },

    getMangaGenres: async () => {
        const headers = await getAuthHeaders(false);
        const response = await mangaClient.get(`${MANGA_API_DOMAIN}/mangas/genres`, { headers });
        return Array.isArray(response.data) ? response.data : [];
    },

    getMangaReviews: async (mangaId, { page = 0, size = 10 } = {}) => {
        const headers = await getAuthHeaders(false);
        const response = await mangaClient.get(`${MANGA_API_DOMAIN}/mangas/${mangaId}/reviews`, {
            params: { page, size },
            headers,
        });
        return response.data;
    },

    // Resolves to null when the user has not reviewed this manga (204).
    getMyMangaReview: async (mangaId) => {
        const headers = await getAuthHeaders(true);
        const response = await mangaClient.get(`${MANGA_API_DOMAIN}/mangas/${mangaId}/reviews/me`, { headers });
        return response.status === 204 || !response.data ? null : response.data;
    },

    saveMangaReview: async (mangaId, { stars, message }) => {
        const headers = await getAuthHeaders(true);
        const response = await mangaClient.put(`${MANGA_API_DOMAIN}/mangas/${mangaId}/reviews`, { stars, message }, { headers });
        return response.data;
    },

    deleteMangaReview: async (reviewId) => {
        const headers = await getAuthHeaders(true);
        await mangaClient.delete(`${MANGA_API_DOMAIN}/reviews/${reviewId}`, { headers });
    },

    getRecentMangaReviews: async ({ page = 0, size = 20 } = {}) => {
        const headers = await getAuthHeaders(true);
        const response = await mangaClient.get(`${MANGA_API_DOMAIN}/moderation/reviews`, {
            params: { page, size },
            headers,
        });
        return response.data;
    },

    getManga: async (slug, options = {}) => {
        const headers = await getAuthHeaders(false);
        const params = options?.includeDeleted ? { includeDeleted: true } : {};
        const response = await mangaClient.get(`${MANGA_API_DOMAIN}/mangas/${slug}`, { headers, params });
        return response.data;
    },

    getChapters: async (mangaId, options = {}) => {
        const headers = await getAuthHeaders(false);
        const params = options?.includeDeleted ? { includeDeleted: true } : {};
        const response = await mangaClient.get(`${MANGA_API_DOMAIN}/mangas/${mangaId}/chapters`, { headers, params });
        return response.data;
    },

    getUserSettings: async () => {
        const headers = await getAuthHeaders(true);
        const response = await mangaClient.get(`${MANGA_API_DOMAIN}/users/settings`, { headers });
        return response.data;
    },

    updateUserSettings: async (settings) => {
        const headers = await getAuthHeaders(true);
        const payload = { ...(settings || {}) };
        if (payload.showNsfw === undefined && payload.show_nsfw !== undefined) {
            payload.showNsfw = payload.show_nsfw;
        }
        delete payload.show_nsfw;
        const response = await mangaClient.patch(`${MANGA_API_DOMAIN}/users/settings`, payload, { headers });
        return response.data;
    },

    getUserLists: async (status) => {
        const headers = await getAuthHeaders(true);
        const response = await mangaClient.get(`${MANGA_API_DOMAIN}/users/lists`, {
            params: status ? { status } : {},
            headers,
        });
        return response.data;
    },

    updateUserList: async (payload) => {
        const headers = await getAuthHeaders(true);
        const response = await mangaClient.put(`${MANGA_API_DOMAIN}/users/lists`, payload, { headers });
        return response.data;
    },

    getPageComments: async (chapterId, pageNumber) => {
        const headers = await getAuthHeaders(false);
        const response = await mangaClient.get(`${MANGA_API_DOMAIN}/comments/chapters/${chapterId}/pages/${pageNumber}`, { headers });
        return response.data;
    },

    leavePageComment: async (chapterId, pageNumber, text) => {
        const headers = await getAuthHeaders(true);
        const response = await mangaClient.post(
            `${MANGA_API_DOMAIN}/comments/chapters/${chapterId}/pages/${pageNumber}`,
            { text },
            { headers }
        );
        return response.data;
    },

    createChapter: async (mangaId, payload) => {
        const headers = await getAuthHeaders(true);
        const response = await mangaClient.post(`${MANGA_API_DOMAIN}/mangas/${mangaId}/chapters`, payload, { headers });
        return response.data;
    },

    updateChapter: async (chapterId, payload) => {
        const headers = await getAuthHeaders(true);
        const response = await mangaClient.patch(`${MANGA_API_DOMAIN}/chapters/${chapterId}`, payload, { headers });
        return response.data;
    },

    getChapterPages: async (chapterId) => {
        const headers = await getAuthHeaders(false);
        const response = await mangaClient.get(`${MANGA_API_DOMAIN}/chapters/${chapterId}/pages`, { headers });
        return response.data;
    },

    setChapterPages: async (chapterId, pages, options = {}) => {
        const headers = await getAuthHeaders(true);
        const payload = Array.isArray(pages) ? pages : (pages?.pages || []);
        const params = options?.append ? { append: true } : {};
        const response = await mangaClient.post(`${MANGA_API_DOMAIN}/chapters/${chapterId}/pages`, payload, {
            headers,
            params,
        });
        return response.data;
    },

    deleteChapter: async (chapterId) => {
        const headers = await getAuthHeaders(true);
        const response = await mangaClient.delete(`${MANGA_API_DOMAIN}/chapters/${chapterId}`, { headers });
        return response.data;
    },

    restoreChapter: async (chapterId) => {
        const headers = await getAuthHeaders(true);
        const response = await mangaClient.post(`${MANGA_API_DOMAIN}/chapters/${chapterId}/restore`, null, { headers });
        return response.data;
    },

    updateTeam: async (teamId, payload) => {
        const headers = await getAuthHeaders(true);
        const response = await mangaClient.patch(`${MANGA_API_DOMAIN}/teams/${teamId}`, payload, { headers });
        return response.data;
    },

    deleteTeam: async (teamId) => {
        const headers = await getAuthHeaders(true);
        const response = await mangaClient.delete(`${MANGA_API_DOMAIN}/teams/${teamId}`, { headers });
        return response.data;
    },

    createTeam: async (payload) => {
        const headers = await getAuthHeaders(true);
        const response = await mangaClient.post(`${MANGA_API_DOMAIN}/teams`, payload, { headers });
        return response.data;
    },

    getMyTeam: async () => {
        const headers = await getAuthHeaders(true);
        const response = await mangaClient.get(`${MANGA_API_DOMAIN}/teams/my`, { headers });
        return response.data;
    },

    getTeamMangas: async (teamId, options = {}) => {
        const headers = await getAuthHeaders(false);
        const params = options?.includeDeleted ? { includeDeleted: true } : {};
        if (options?.page !== undefined && options?.page !== null) {
            params.page = options.page;
        }
        if (options?.size !== undefined && options?.size !== null) {
            params.size = options.size;
        }
        const response = await mangaClient.get(`${MANGA_API_DOMAIN}/teams/${teamId}/mangas`, { headers, params });
        return response.data;
    },

    getTeam: async (teamId) => {
        const headers = await getAuthHeaders(false);
        const response = await mangaClient.get(`${MANGA_API_DOMAIN}/teams/${teamId}`, { headers });
        return response.data;
    },

    getTeamMembers: async (teamId) => {
        const headers = await getAuthHeaders(false);
        const response = await mangaClient.get(`${MANGA_API_DOMAIN}/teams/${teamId}/members`, { headers });
        return response.data;
    },

    getTeamPendingChapterLimit: async (teamId) => {
        const headers = await getAuthHeaders(true);
        const response = await mangaClient.get(`${MANGA_API_DOMAIN}/teams/${teamId}/chapters/pending-limit`, { headers });
        return response.data;
    },

    getTeamMangasPublic: async (teamId, options = {}) => {
        const headers = await getAuthHeaders(false);
        const params = options?.includeDeleted ? { includeDeleted: true } : {};
        if (options?.page !== undefined && options?.page !== null) {
            params.page = options.page;
        }
        if (options?.size !== undefined && options?.size !== null) {
            params.size = options.size;
        }
        const response = await mangaClient.get(`${MANGA_API_DOMAIN}/teams/${teamId}/mangas`, { headers, params });
        return response.data;
    },

    addTeamMember: async (teamId, userId) => {
        const headers = await getAuthHeaders(true);
        const response = await mangaClient.post(`${MANGA_API_DOMAIN}/teams/${teamId}/members`, { userId }, { headers });
        return response.data;
    },

    removeTeamMember: async (teamId, userId) => {
        const headers = await getAuthHeaders(true);
        const response = await mangaClient.delete(`${MANGA_API_DOMAIN}/teams/${teamId}/members/${userId}`, { headers });
        return response.data;
    },

    transferTeamOwnership: async (teamId, userId) => {
        const headers = await getAuthHeaders(true);
        const response = await mangaClient.post(`${MANGA_API_DOMAIN}/teams/${teamId}/members/${userId}/transfer-ownership`, null, { headers });
        return response.data;
    },

    leaveTeam: async () => {
        const headers = await getAuthHeaders(true);
        const response = await mangaClient.post(`${MANGA_API_DOMAIN}/teams/my/leave`, null, { headers });
        return response.data;
    },

    getTeamAudit: async (teamId) => {
        const headers = await getAuthHeaders(true);
        const response = await mangaClient.get(`${MANGA_API_DOMAIN}/teams/${teamId}/audit`, { headers });
        return response.data;
    },

    createManga: async (payload) => {
        const headers = await getAuthHeaders(true);
        const response = await mangaClient.post(`${MANGA_API_DOMAIN}/mangas`, payload, { headers });
        return response.data;
    },

    updateManga: async (mangaId, payload) => {
        const headers = await getAuthHeaders(true);
        const response = await mangaClient.patch(`${MANGA_API_DOMAIN}/mangas/${mangaId}`, payload, { headers });
        return response.data;
    },

    deleteManga: async (mangaId) => {
        const headers = await getAuthHeaders(true);
        const response = await mangaClient.delete(`${MANGA_API_DOMAIN}/mangas/${mangaId}`, { headers });
        return response.data;
    },

    restoreManga: async (mangaId) => {
        const headers = await getAuthHeaders(true);
        const response = await mangaClient.post(`${MANGA_API_DOMAIN}/mangas/${mangaId}/restore`, null, { headers });
        return response.data;
    },

    restoreModerationManga: async (mangaId) => {
        const headers = await getAuthHeaders(true);
        const response = await mangaClient.post(`${MANGA_API_DOMAIN}/moderation/mangas/${mangaId}/restore`, null, { headers });
        return response.data;
    },

    transferManga: async (mangaId, teamId) => {
        const headers = await getAuthHeaders(true);
        const response = await mangaClient.post(`${MANGA_API_DOMAIN}/mangas/${mangaId}/transfer`, { teamId }, { headers });
        return response.data;
    },

    approveChapter: async (chapterId) => {
        const headers = await getAuthHeaders(true);
        const response = await mangaClient.post(`${MANGA_API_DOMAIN}/moderation/chapters/${chapterId}/approve`, null, { headers });
        return response.data;
    },

    getModerationMangas: async (options = {}) => {
        const headers = await getAuthHeaders(true);
        const params = {};
        if (options.status) {
            params.status = options.status;
        }
        if (options.order) {
            params.order = options.order;
        }
        const response = await mangaClient.get(`${MANGA_API_DOMAIN}/moderation/mangas`, { headers, params });
        return response.data;
    },

    getInactiveMangas: async (options = {}) => {
        const headers = await getAuthHeaders(true);
        const params = {};
        if (options.days) {
            params.days = options.days;
        }
        const response = await mangaClient.get(`${MANGA_API_DOMAIN}/moderation/mangas/inactive`, { headers, params });
        return response.data;
    },

    getModerationChapters: async (options = {}) => {
        const headers = await getAuthHeaders(true);
        const params = {};
        if (options.status) {
            params.status = options.status;
        }
        if (options.order) {
            params.order = options.order;
        }
        if (options.page !== undefined && options.page !== null) {
            params.page = options.page;
        }
        if (options.size !== undefined && options.size !== null) {
            params.size = options.size;
        }
        const response = await mangaClient.get(`${MANGA_API_DOMAIN}/moderation/chapters`, { headers, params });
        return response.data;
    },

    rejectChapter: async (chapterId, reason) => {
        const headers = await getAuthHeaders(true);
        const response = await mangaClient.post(
            `${MANGA_API_DOMAIN}/moderation/chapters/${chapterId}/reject`,
            { reason },
            { headers }
        );
        return response.data;
    },

    restoreModerationChapter: async (chapterId) => {
        const headers = await getAuthHeaders(true);
        const response = await mangaClient.post(`${MANGA_API_DOMAIN}/moderation/chapters/${chapterId}/restore`, null, { headers });
        return response.data;
    },

    approveManga: async (mangaId) => {
        const headers = await getAuthHeaders(true);
        const response = await mangaClient.post(`${MANGA_API_DOMAIN}/moderation/mangas/${mangaId}/approve`, null, { headers });
        return response.data;
    },

    rejectManga: async (mangaId, reason) => {
        const headers = await getAuthHeaders(true);
        const response = await mangaClient.post(
            `${MANGA_API_DOMAIN}/moderation/mangas/${mangaId}/reject`,
            { reason },
            { headers }
        );
        return response.data;
    },

    getModerationTeams: async (options = {}) => {
        const headers = await getAuthHeaders(true);
        const params = {};
        if (options.status) {
            params.status = options.status;
        }
        if (options.order) {
            params.order = options.order;
        }
        const response = await mangaClient.get(`${MANGA_API_DOMAIN}/moderation/teams`, { params, headers });
        return response.data;
    },

    getModerationTeamAudit: async (teamId) => {
        const headers = await getAuthHeaders(true);
        const response = await mangaClient.get(`${MANGA_API_DOMAIN}/moderation/teams/${teamId}/audit`, { headers });
        return response.data;
    },

    approveTeam: async (teamId) => {
        const headers = await getAuthHeaders(true);
        const response = await mangaClient.post(`${MANGA_API_DOMAIN}/moderation/teams/${teamId}/approve`, null, { headers });
        return response.data;
    },

    rejectTeam: async (teamId, reason) => {
        const headers = await getAuthHeaders(true);
        const response = await mangaClient.post(
            `${MANGA_API_DOMAIN}/moderation/teams/${teamId}/reject`,
            { reason },
            { headers }
        );
        return response.data;
    },

    deleteRejectedTeam: async (teamId) => {
        const headers = await getAuthHeaders(true);
        const response = await mangaClient.delete(`${MANGA_API_DOMAIN}/moderation/teams/${teamId}`, { headers });
        return response.data ?? response;
    },

    uploadFile: async (file, bucket, storageBaseUrl) => {
        const headers = await getAuthHeaders(true);
        const target = (!bucket || !storageBaseUrl)
            ? await getStorageTarget({ required: true })
            : {};
        const resolvedBucket = bucket || target.bucket;
        const resolvedBase = storageBaseUrl || target.publicBaseUrl || pickStorageBaseUrl(resolvedBucket);
        if (!resolvedBase) {
            throw new Error('Storage target is not available');
        }
        rememberStorageBase(resolvedBucket, resolvedBase);
        const formData = new FormData();
        formData.append('file', file);
        if (resolvedBucket) {
            formData.append('bucket', resolvedBucket);
        }
        const response = await mangaClient.post(`${resolvedBase}/upload`, formData, {
            headers: {
                ...headers,
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    },

    attachFile: async (fileId, bucket, storageBaseUrl) => {
        const headers = await getAuthHeaders(true);
        const base = pickStorageBaseUrl(bucket, storageBaseUrl);
        if (!base) {
            throw new Error('Storage base url is not available');
        }
        const url = bucket
            ? `${base}/buckets/${bucket}/files/${fileId}/attach`
            : `${base}/files/${fileId}/attach`;
        const response = await mangaClient.post(url, null, { headers });
        return response.data;
    },

    deleteFile: async (fileId, bucket, storageBaseUrl) => {
        const headers = await getAuthHeaders(true);
        const base = pickStorageBaseUrl(bucket, storageBaseUrl);
        if (!base) {
            throw new Error('Storage base url is not available');
        }
        const url = bucket
            ? `${base}/buckets/${bucket}/files/${fileId}`
            : `${base}/files/${fileId}`;
        const response = await mangaClient.delete(url, { headers });
        return response.data;
    },
};

export default MangaApiService;
